/**
 * Client-side wrappers for the POW portal-api endpoints (ADR-027 §4 +
 * ADR-028 § Lifecycle + ADR-029 §5a/§5b).
 *
 * Each call attaches the subscriber's Cognito id-token via the
 * `Authorization: Bearer` header read from Amplify's session, matching the
 * client-auth Lambda authoriser's expectations.
 *
 * On 4xx the wrappers throw `PowApiError` with the decoded server envelope
 * so the form / status page can surface the structured `error.code` to the
 * user without re-parsing.
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import { tokenMatchesTab } from './tab-identity';
import { activePortalApiBaseUrl } from './zone-session';

export interface PowErrorBody {
  error: {
    code: string;
    message: string;
    [extra: string]: unknown;
  };
}

export class PowApiError extends Error {
  constructor(
    public status: number,
    public body: PowErrorBody | { error?: { code?: string; message?: string } } | undefined,
    message?: string,
  ) {
    super(message ?? (body as PowErrorBody | undefined)?.error?.message ?? `HTTP ${status}`);
    this.name = 'PowApiError';
  }

  get code(): string {
    return (this.body as PowErrorBody | undefined)?.error?.code ?? 'UNKNOWN';
  }
}

async function authHeader(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    // Q13 — never as somebody other than the person this tab is showing (see portal-api.ts).
    if (!tokenMatchesTab(session.tokens?.idToken?.payload?.sub)) return {};
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function apiCall<T>(
  path: string,
  init: RequestInit & { allowedStatuses?: number[] } = {},
): Promise<T> {
  const { allowedStatuses, ...rest } = init;
  const headers = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
    ...(rest.headers ?? {}),
  };
  const res = await fetch(`${activePortalApiBaseUrl()}${path}`, { ...rest, headers });
  if (!res.ok && !(allowedStatuses ?? []).includes(res.status)) {
    let body: unknown;
    try { body = await res.json(); } catch { /* non-JSON */ }
    throw new PowApiError(res.status, body as PowErrorBody | undefined);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// =============================================================================
// GET /portal/pow/prefill-token
// =============================================================================

export interface PrefillTokenResponse {
  token: string;
  issuedAt: number;
  expiresAt: number;
  ttlSeconds: number;
  prefill: {
    customerNumber: string;
    email: string;
    businessName: string;
    websiteUrl: string;
  };
}

export async function fetchPrefillToken(): Promise<PrefillTokenResponse> {
  return apiCall<PrefillTokenResponse>('/portal/pow/prefill-token');
}

// =============================================================================
// POST /portal/pow/submit
// =============================================================================

export interface PowSubmitInput {
  preFillToken?: string;
  businessName: string;
  websiteUrl: string;
  registrationNumber?: string;
  jurisdiction: 'AU' | 'NZ' | 'US';
  descriptionOfPractice: string;
  customerNumber: string;
  // ADR-077 A1.4 — "Start fresh" at the entry fork supersedes this in-progress
  // application instead of being blocked by APPLICATION_IN_PROGRESS.
  supersedeApplicationId?: string;
}

export interface PowSubmitResponse {
  applicationId: string;
  status: 'pending_validation';
  submittedAt: string;
}

export async function submitPowApplication(
  input: PowSubmitInput,
): Promise<PowSubmitResponse> {
  return apiCall<PowSubmitResponse>('/portal/pow/submit', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// =============================================================================
// GET /portal/pow/status   +   GET /portal/pow/application
// =============================================================================

export interface PowStatusResponse {
  applicationId: string;
  status: string;
  score: number | null;
  submittedAt: string;
  lastAssessedAt: string | null;
  assessmentCount: number;
  businessName: string;
  websiteUrl: string;
  jurisdiction: string;
  scoringNote: string | null;
  canReassess: boolean;
}

export async function fetchPowStatus(): Promise<PowStatusResponse | null> {
  try {
    return await apiCall<PowStatusResponse>('/portal/pow/status');
  } catch (err) {
    if (err instanceof PowApiError && err.status === 404) return null;
    throw err;
  }
}

// =============================================================================
// POST /portal/pow/reassess
// =============================================================================

export interface PowReassessInput {
  applicationId: string;
  websiteUrl: string;
}

export interface PowReassessResponse {
  applicationId: string;
  status: 'pending_reassessment';
  reassessedAt: string;
}

export async function submitReassessment(
  input: PowReassessInput,
): Promise<PowReassessResponse> {
  return apiCall<PowReassessResponse>('/portal/pow/reassess', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
